import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_tabs.scss'

import addSubcomponents from '../utils/addSubcomponents'
import {
  PlacementNS,
  Props,
  NavAttributes,
  DivAttributes,
  SpanAttributes,
} from '../utils/types'
import isReactElement from '../utils/isReactElement'
import { useBoundingRect } from '../state/BoundingRect'
import { OverlayTriggerVariantNS } from '../state/OverlayTrigger'
import { useEvent } from '../state/Event'
import Card from './Card'
import Menu, { Options, Item } from './Menu'
import { OverlayTriggerAdapter as OverlayTrigger } from './OverlayTriggerAdapter'

export namespace TabVariantNS {
  export const none = ''
  export const active = 'active'
  export const hover = 'hover'
  export const dropdown = 'dropdown'
  export const overflowing = 'overflowing'
}

export namespace TabsVariantNS {
  export const none = ''
  export const allOverflowing = 'all-overflowing'
}

type TabVariant =
  | typeof TabVariantNS.active
  | typeof TabVariantNS.dropdown
  | typeof TabVariantNS.hover
  | typeof TabVariantNS.none
  | typeof TabVariantNS.overflowing

type TabsVariant =
  | typeof TabsVariantNS.allOverflowing
  | typeof TabsVariantNS.none

export interface TabProps extends Props {
  active?: boolean
  dropdown?: boolean
  overflowing?: boolean
  variant?: TabVariant
}

export interface TabsProps extends Props {
  variant?: TabsVariant
}

export interface TabDropdownProps extends Props {
  visible?: boolean
}

const cx = bind(styles)

export const Link = React.forwardRef<HTMLSpanElement, SpanAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <span ref={ref} className={cx('core-tabs__link', className)} {...props}>
      {children}
    </span>
  )
)

export const Tab = React.forwardRef<HTMLDivElement, DivAttributes & TabProps>(
  (
    {
      active = false,
      children,
      className,
      dropdown = false,
      overflowing = false,
      variant = TabVariantNS.none,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cx(
        'core-tabs__tab',
        {
          [`core-tabs__tab--${TabVariantNS.active}`]: active,
          [`core-tabs__tab--${TabVariantNS.dropdown}`]: dropdown,
          [`core-tabs__tab--${TabVariantNS.overflowing}`]: overflowing,
          [`core-tabs__tab--${variant}`]: variant,
        },
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)

export const Dropdown = React.forwardRef<
  HTMLDivElement,
  DivAttributes & TabDropdownProps
>(({ children, className }, ref) => {
  const isActive = React.Children.toArray(children).some(
    child => isReactElement(child) && child.props.active
  )

  const menu = (
    <Card className={cx('core-tabs__dropdown', className)}>
      <Menu>
        <Options>
          {React.Children.map(children, (child, i) => {
            if (isReactElement<TabProps>(child)) {
              return (
                <Item key={i} item={null} selected={child.props.active}>
                  {React.cloneElement(child as any, { ref: null })}
                </Item>
              )
            }
          })}
        </Options>
      </Menu>
    </Card>
  )

  return (
    <OverlayTrigger
      trigger={OverlayTriggerVariantNS.hover}
      placement={PlacementNS.bottomRight}
      overlay={menu}
    >
      <Tab dropdown active={isActive}>
        <Link>More</Link>
      </Tab>
    </OverlayTrigger>
  )
})

export const Tabs = React.forwardRef<HTMLDivElement, NavAttributes & TabsProps>(
  ({ children, className, variant, ...props }, ref) => {
    const filtered = React.Children.toArray(children).filter(child => child)

    const { value: resize } = useEvent({ event: 'resize' })

    const container = useBoundingRect({ dependencies: [resize] })

    const dropdown = useBoundingRect({ dependencies: [resize] })

    const rects = filtered.map(() => useBoundingRect({}))

    const { subtotals } = rects.reduce(
      ({ total, subtotals }, { rect }) => ({
        subtotals: [...subtotals, total + rect.width],
        total: total + rect.width,
      }),
      { subtotals: new Array(), total: 0 }
    )

    const index = subtotals.findIndex(
      (val: number) => val >= container.rect.width - dropdown.rect.width
    )

    return (
      <nav
        ref={ref}
        className={cx('core-tabs', className, {
          ['core-tabs--all-overflowing']: index === 0,
        })}
        {...props}
      >
        <div ref={container.ref} className={cx('core-tabs__list')}>
          {filtered.map((child, i) => {
            if (isReactElement(child))
              return React.cloneElement(child, {
                ref: rects[i].ref,
                overflowing: index >= 0 && i >= index,
              })
          })}
          <div
            ref={dropdown.ref}
            className={cx('core-tabs__more', {
              'core-tabs__more--overflowing': index >= 0,
            })}
          >
            <Dropdown ref={dropdown.ref}>{filtered.slice(index)}</Dropdown>
          </div>
        </div>
      </nav>
    )
  }
)

Tabs.displayName = 'Tabs'

Link.displayName = 'Tabs.Link'

Tab.displayName = 'Tabs.Tab'

export default addSubcomponents(
  {
    Link,
    Tab,
  },
  Tabs
)
